import React from 'react';
import {
  Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Schedule, AutoMode, BlockOutlined, PlayArrow } from '@mui/icons-material';
import Panel from '../crt/Panel';
import { episodesAPI } from '../../services/api';

type PendingEpisode = {
  site: string;
  post_id: number;
  post_title: string;
  post_name: string;
  post_status: string;
  post_date: string | null;
  canonical_url: string;
  release_at: string | null;        // may be null for in-progress drafts
  auto_publish: boolean;
  published_at: string | null;
  megaphone_id: string | null;
  megaphone_url: string | null;
  youtube_id: string | null;
  youtube_url: string | null;
  website_url: string | null;
  yt_renamed: boolean;              // YT title flipped clickbait→SEO yet?
  yt_renamed_at: string | null;
  yt_rename_at: string | null;      // scheduled rename time (if any)
  yt_seo_title: string | null;
  yt_embedded: boolean;             // YT iframe embedded into the page body
  transcript_complete: boolean;     // transcript applied to the WP page
  next_step: 'megaphone' | 'publish' | 'done';
};

type Props = {
  // Wire from parent so clicking Resume hydrates the wizard's state
  // and scrolls to Step 2. Optional — if not passed, the row's Resume
  // button is hidden (component degrades gracefully).
  onResume?: (postId: number, site: string) => void;
};

const fmtRelative = (iso: string, now: Date): string => {
  const t = new Date(iso).getTime();
  const diff = t - now.getTime();
  const sign = diff < 0 ? '-' : '';
  const abs = Math.abs(diff);
  const m = Math.round(abs / 60000);
  if (m < 60) return `${sign}${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${sign}${h}h`;
  const d = Math.round(h / 24);
  return `${sign}${d}d`;
};

const PendingReleasesPanel: React.FC<Props> = ({ onResume }) => {
  const [siteFilter, setSiteFilter] = React.useState<'all' | 'spoutlore' | 'mallbrats'>('all');
  const { data, isLoading } = useQuery({
    queryKey: ['pending-releases', siteFilter],
    queryFn: () => episodesAPI.pendingReleases({
      site: siteFilter === 'all' ? undefined : siteFilter,
      days_ahead: 90,
      days_back: 7,
    }),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const now = data ? new Date(data.now) : new Date();
  const episodes: PendingEpisode[] = data?.episodes || [];

  // In-progress drafts (no release_at, not published) are wizard-
  // abandoned and need resuming. Surface them prominently.
  const inProgress = episodes.filter((e) => !e.release_at && !e.published_at);
  const upcoming = episodes.filter((e) => e.release_at && !e.published_at && new Date(e.release_at) >= now);
  const overdue  = episodes.filter((e) => e.release_at && !e.published_at && new Date(e.release_at) < now);
  const shipped  = episodes.filter((e) => e.published_at);

  const renderRow = (e: PendingEpisode) => {
    const rel = e.release_at ? new Date(e.release_at) : null;
    const isOverdue = !e.published_at && rel != null && rel < now;
    const wpUrl = e.site === 'spoutlore'
      ? `https://www.spoutlore.com/wp-admin/post.php?post=${e.post_id}&action=edit`
      : `https://www.mallbratspodcast.com/wp-admin/post.php?post=${e.post_id}&action=edit`;
    return (
      <TableRow key={`${e.site}-${e.post_id}`} hover sx={
        isOverdue ? { bgcolor: 'rgba(255,176,0,0.05)' } :
        (!e.release_at && !e.published_at) ? { bgcolor: 'rgba(0,200,255,0.05)' } :
        undefined
      }>
        <TableCell>
          <Chip
            label={e.site === 'spoutlore' ? 'SL' : 'MB'}
            size="small"
            color={e.site === 'spoutlore' ? 'primary' : 'secondary'}
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <a href={wpUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
            {e.post_title}
          </a>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
            #{e.post_id} · {e.post_name}
          </Typography>
        </TableCell>
        <TableCell>
          {rel ? (
            <>
              <Tooltip title={e.release_at}>
                <Typography variant="body2" fontFamily="monospace">
                  {rel.toLocaleString()}
                </Typography>
              </Tooltip>
              <Typography variant="caption" color={isOverdue ? 'warning.main' : 'text.secondary'}>
                {e.published_at ? '(shipped)' : `${fmtRelative(e.release_at!, now)} from now`}
              </Typography>
            </>
          ) : (
            <Typography variant="caption" color="info.main">
              no release date set
              {e.post_date && (
                <Typography variant="caption" component="span" color="text.secondary" sx={{ ml: 0.5 }}>
                  (created {fmtRelative(e.post_date, now)} ago)
                </Typography>
              )}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {e.published_at ? (
            <Chip label="PUBLISHED" size="small" color="success" icon={<CheckCircle />} />
          ) : e.auto_publish ? (
            <Chip label="AUTO" size="small" color="primary" icon={<AutoMode />} />
          ) : (
            <Chip label="MANUAL" size="small" variant="outlined" icon={<BlockOutlined />} />
          )}
        </TableCell>
        <TableCell>
          {e.megaphone_id ? (
            <Tooltip title={`Open in Megaphone CMS · ${e.megaphone_id}`}>
              <Chip
                label="✓ MP" size="small" variant="outlined" color="success"
                component="a" clickable
                href={e.megaphone_url || undefined} target="_blank"
              />
            </Tooltip>
          ) : (
            <Chip label="—" size="small" variant="outlined" />
          )}
        </TableCell>
        <TableCell>
          {e.youtube_id ? (
            <Tooltip title={`Open on YouTube · ${e.youtube_id}`}>
              <Chip
                label="✓ YT"
                size="small"
                variant="outlined"
                color="success"
                component="a"
                clickable
                href={e.youtube_url || `https://www.youtube.com/watch?v=${e.youtube_id}`}
                target="_blank"
              />
            </Tooltip>
          ) : (
            <Chip label="—" size="small" variant="outlined" />
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {/* YouTube title flipped clickbait→SEO yet? */}
            {e.youtube_id && (
              e.yt_renamed ? (
                <Tooltip title={`YouTube title renamed to SEO${e.yt_seo_title ? `: "${e.yt_seo_title}"` : ''}${e.yt_renamed_at ? ` (${new Date(e.yt_renamed_at).toLocaleString()})` : ''}`}>
                  <Chip label="title ✓" size="small" color="success" variant="outlined" />
                </Tooltip>
              ) : e.yt_rename_at ? (
                <Tooltip title={`YouTube title rename scheduled for ${new Date(e.yt_rename_at).toLocaleString()}${e.yt_seo_title ? ` → "${e.yt_seo_title}"` : ''}`}>
                  <Chip label="title ⏳" size="small" color="warning" variant="outlined" />
                </Tooltip>
              ) : (
                <Tooltip title="no SEO rename scheduled">
                  <Chip label="title —" size="small" variant="outlined" />
                </Tooltip>
              )
            )}
            {/* YT video pulled from the channel + embedded into the page */}
            <Tooltip title={e.yt_embedded ? 'YouTube video embedded on the page' : 'YouTube embed not on the page yet'}>
              <Chip label={e.yt_embedded ? 'embed ✓' : 'embed —'} size="small"
                    color={e.yt_embedded ? 'success' : 'default'} variant="outlined" />
            </Tooltip>
            {/* Transcript applied to the WP page */}
            <Tooltip title={e.transcript_complete ? 'transcript on the page' : 'no transcript on the page yet'}>
              <Chip label={e.transcript_complete ? 'txt ✓' : 'txt —'} size="small"
                    color={e.transcript_complete ? 'success' : 'default'} variant="outlined" />
            </Tooltip>
            {/* Link to the live page */}
            {e.website_url && e.published_at && (
              <Tooltip title="Open the live page">
                <Chip label="web ↗" size="small" variant="outlined"
                      component="a" clickable href={e.website_url} target="_blank" />
              </Tooltip>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="caption" fontFamily="monospace">
            {e.post_status}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            next: <strong>{e.next_step}</strong>
          </Typography>
        </TableCell>
        <TableCell>
          {!e.published_at && onResume && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<PlayArrow />}
              onClick={() => onResume(e.post_id, e.site)}
            >
              Resume
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Panel label="PENDING :: WHAT THE CRON IS WATCHING">
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            Episodes with a release-date set, in the window [now - 7 days,
            now + 90 days]. The publish_sync cron runs every 5 minutes and
            flips anything past its release_at when auto-publish is on.
            <Schedule fontSize="inherit" sx={{ verticalAlign: 'middle', mx: 0.5 }} />
            polls every 60s.
          </Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Site</InputLabel>
            <Select
              label="Site"
              value={siteFilter}
              onChange={(e) => setSiteFilter(e.target.value as any)}
            >
              <MenuItem value="all">Both shows</MenuItem>
              <MenuItem value="spoutlore">Spout Lore</MenuItem>
              <MenuItem value="mallbrats">Mall Brats</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
          <Typography variant="caption">
            <strong style={{ color: '#00c8ff' }}>{inProgress.length}</strong> in progress (resume)
          </Typography>
          <Typography variant="caption">
            <strong style={{ color: '#ffb000' }}>{overdue.length}</strong> overdue
          </Typography>
          <Typography variant="caption">
            <strong style={{ color: '#00ff41' }}>{upcoming.length}</strong> upcoming (next 90 days)
          </Typography>
          <Typography variant="caption">
            <strong style={{ color: '#5aaa5a' }}>{shipped.length}</strong> shipped (last 7 days)
          </Typography>
        </Stack>

        {isLoading && <Typography variant="caption">loading…</Typography>}

        {!isLoading && episodes.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nothing pending. Create a draft via the Publish wizard above; it'll
            show up here as "in progress" until you finish Step 2 + publish.
          </Typography>
        )}

        {episodes.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SHOW</TableCell>
                  <TableCell>EPISODE</TableCell>
                  <TableCell>RELEASE AT</TableCell>
                  <TableCell>MODE</TableCell>
                  <TableCell>MP</TableCell>
                  <TableCell>YT</TableCell>
                  <TableCell>ON SITE</TableCell>
                  <TableCell>WP STATUS</TableCell>
                  <TableCell>RESUME</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* In-progress drafts at the top (most likely to need
                    attention right now), then overdue, then upcoming,
                    then recently shipped. */}
                {[...inProgress, ...overdue, ...upcoming, ...shipped].map(renderRow)}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Panel>
  );
};

export default PendingReleasesPanel;
